// src/modules/app-views/crm/pages/Kanban/components/KanbanCard.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Card, Tag, Tooltip, Typography } from 'antd';
import { useState, useEffect } from 'react';
import { IoHandLeftOutline } from 'react-icons/io5';
import { CgPlayPauseO, CgPlayStopO } from 'react-icons/cg';
import {
  CalendarOutlined,
  ClockCircleOutlined,
  CommentOutlined,
  DollarOutlined,
  EditOutlined,
  HistoryOutlined,
  MessageOutlined,
  PaperClipOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  ShopOutlined,
  SwapOutlined,
  UserOutlined,
} from '@ant-design/icons';
import showKanbanEditModal from '../modals/EditOpportunityModal';
import showEditOpportunityFormModal from '../modals/EditOpportunityFormModal';
import showTransferOpportunityModal from '../modals/TransferOpportunityModal';
import { Draggable } from '@hello-pangea/dnd';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import useZustand from 'hooks/useZustand';
import { buildOriginIcon } from 'components/JarvislyComponents/DocumentInfoIcons';
import SimpleAvatarGroup from 'components/SimpleComponents/SimpleAvatarGroup';
import Blob from 'components/core/Blob';
import appService from 'services/appService';
import crmService from '../../../service';
import translateX from 'utils/translateX';
import { toCapitalCase } from 'utils/helpers';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const KanbanCard = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { card, index, isLast, columnSla, boardUid, columnUid, standalone } = props;
  const setPageStore = useZustand('module', 'setPage');
  const currentTheme = useZustand('theme', 'currentTheme');
  const storedParameters = useZustand('app', 'parameters');
  const storedUser = useZustand('auth', 'user');
  const navigate = useNavigate();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const entityName = card?.__entity?.name || card?.__entity?.firstName || '';
  // personContactName is the individual person when entityId points to a company
  const personContactName =
    card?.__personContact?.name || card?.__personContact?.firstName || '';
  const showPersonContact = personContactName && personContactName !== entityName;
  const hasValue = card?.approximateValue > 0;
  const hasDeadline = !!card?.deadline;
  const callCount = card?.callCount || 0;
  const isDark = currentTheme === 'dark';
  const isCompany = card?.__entity?.entityType === 'company';
  // createdAt/updatedAt are projected directly by the board pipeline
  const createdAt = card?.createdAt;
  const updatedAt = card?.updatedAt;
  // team members (all who handled this opportunity)
  const teamMembers = card?.__team || [];
  // Attendance state: started and not filed and not rescheduled = actively attending
  const isFiled = !!card?.filedAt;
  const isRescheduled = !!card?.attendanceRescheduledAt && !isFiled;
  // NOTE: isAttending depends on the localAttendanceStarted state, so it is
  // derived later (block 6), after that state is declared, to avoid a TDZ error.
  // Quantitative indicators projected by board pipeline
  const followUpCount = card?.followUpCount || 0;
  const attachmentCount = card?.attachmentCount || 0;

  // Lifetime: elapsed time since opportunity was created.
  // Frozen at updatedAt when finalized (contract_closed or lost).
  const isFinalized = card?.status === 'contract_closed' || card?.status === 'lost';
  const lifetimeRef = isFinalized ? card?.updatedAt : null; // null = live (fromNow uses current time)
  const lifetimeText = createdAt
    ? lifetimeRef
      ? dayjs(createdAt).from(dayjs(lifetimeRef)) // frozen: "há X dias" relative to close date
      : dayjs(createdAt).fromNow()                // live: relative to now
    : null;

  // temperature accent color
  const temperatures = crmService.getTemperatures();
  const temperature = temperatures.find(t => t.uid === card?.temperatureUid);
  const tempColor = temperature?.color || null;

  // SLA config from the column and the board's business calendar.
  // Supports new format { businessTime: { minutes }, elapsedTime: { minutes } }
  // and old flat format { businessMinutes, elapsedHours } for backward compat.
  const slaLimitBusinessMinutes =
    columnSla?.businessTime?.minutes || columnSla?.businessMinutes || 0;
  const slaLimitHours = columnSla?.elapsedTime
    ? (columnSla.elapsedTime.minutes || 0) / 60
    : columnSla?.elapsedHours || 0;

  // Human-readable SLA config labels for the badge tooltip
  const bizSlaLabel =
    columnSla?.businessTime?.value > 0
      ? `${columnSla.businessTime.value} ${translateX(columnSla.businessTime.unit)}`
      : slaLimitBusinessMinutes > 0
        ? `${slaLimitBusinessMinutes} ${translateX('minutes')}`
        : null;
  const elapsedSlaLabel =
    columnSla?.elapsedTime?.value > 0
      ? `${columnSla.elapsedTime.value} ${translateX(columnSla.elapsedTime.unit)}`
      : slaLimitHours > 0
        ? `${Math.round(slaLimitHours * 10) / 10} ${translateX('hours')}`
        : null;
  const slaConfigTooltip = [
    bizSlaLabel && `SLA Comercial: ${bizSlaLabel}`,
    elapsedSlaLabel && `SLA Corrido: ${elapsedSlaLabel}`,
  ]
    .filter(Boolean)
    .join(' | ');
  const boards = storedParameters?.modules?.crm?.boards || [];
  const board = boards.find(b => b.uid === boardUid) || null;
  const boardBusinessHours = board?.businessHours || null;
  const boardHolidays = board?.holidays || [];

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [isHovered, setIsHovered] = useState(false);
  // Optimistic attendance state — avoids full kanban reload after startAttendance
  const [localAttendanceStarted, setLocalAttendanceStarted] = useState(false);
  // Wall-clock elapsed ms — used for the elapsed-SLA badge
  const [elapsedMs, setElapsedMs] = useState(() => crmService.computeElapsedMs(card));
  // Business-hours seconds elapsed since card entered this column (primary badge).
  // Initialized eagerly so the badge shows the correct value before the first tick.
  const [bizSecElapsed, setBizSecElapsed] = useState(() => {
    if (!slaLimitBusinessMinutes) return 0;
    const schedule = boardBusinessHours || crmService.DEFAULT_BUSINESS_HOURS;
    return crmService.computeBusinessSecondsElapsed(crmService.getCardInAt(card), schedule, boardHolidays);
  });
  // Whether the current wall-clock time falls inside business hours (drives icon + freeze UX)
  const [isInBizHours, setIsInBizHours] = useState(() => {
    if (!slaLimitBusinessMinutes) return true;
    const schedule = boardBusinessHours || crmService.DEFAULT_BUSINESS_HOURS;
    return crmService.checkIsInBusinessHours(schedule, boardHolidays);
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const hasSla = slaLimitBusinessMinutes > 0 || slaLimitHours > 0;
    if (!hasSla || isRescheduled || isFinalized) return;

    const tick = () => {
      const ms = crmService.computeElapsedMs(card);
      setElapsedMs(ms);
      if (slaLimitBusinessMinutes > 0) {
        const schedule = boardBusinessHours || crmService.DEFAULT_BUSINESS_HOURS;
        const inAt = crmService.getCardInAt(card);
        setBizSecElapsed(crmService.computeBusinessSecondsElapsed(inAt, schedule, boardHolidays));
        setIsInBizHours(crmService.checkIsInBusinessHours(schedule, boardHolidays));
      }
    };

    tick(); // run immediately so badge is correct on mount
    const id = setInterval(tick, 1_000); // 1-second precision for live countdown
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card._id, slaLimitBusinessMinutes, slaLimitHours, boardBusinessHours, isRescheduled]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleClick = () => {
    setPageStore({
      navigate,
      pageName: 'document',
      params: { _id: card._id },
    });
  };

  const handleEditClick = e => {
    e.stopPropagation();
    const onSuccess = () => window.dispatchEvent(new CustomEvent('crm:kanban:refresh'));

    if (board?.newOpportunityMode === 'custom' && board?.formFields?.length > 0) {
      showEditOpportunityFormModal({ card, onSuccess });
    } else {
      showKanbanEditModal({ card, onSuccess });
    }
  };

  const handleTransferClick = e => {
    e.stopPropagation();
    showTransferOpportunityModal({
      card: { ...card, boardUid, columnUid },
      onSuccess: () => window.dispatchEvent(new CustomEvent('crm:kanban:refresh')),
    });
  };

  const handleStartAttendance = async e => {
    e.stopPropagation();
    if (isAttending) return; // Already attending — no-op
    try {
      await crmService.startAttendance(card._id, {
        accountName: storedUser?.name || null,
        accountAvatarUrl:
          storedUser?.avatar?.base64Thumb ||
          storedUser?.avatar?.thumbUrl ||
          null,
        followUpText: translateX('attendance_started'),
      });
      // Optimistic update — no full kanban reload
      setLocalAttendanceStarted(true);
      appService.message({ flag: 's', text: 'attendance_started_success' });
    } catch (err) {
      console.error('[KanbanCard] startAttendance error:', err);
      appService.message({ flag: 'e', text: 'error_starting_attendance' });
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const cardInAt = crmService.getCardInAt(card);

  // Actively attending = started (server flag OR optimistic local state) and not
  // filed and not rescheduled. Derived here because it reads localAttendanceStarted.
  const isAttending =
    (!!card?.attendanceStartedAt || localAttendanceStarted) && !isFiled && !isRescheduled;

  // Deadline for business SLA — walks forward through business-hours schedule.
  // Falls back to DEFAULT_BUSINESS_HOURS (Mon–Fri 08:00–18:00) when the board
  // has no businessHours configured.
  const slaDeadlineBusiness = (() => {
    if (!cardInAt || !slaLimitBusinessMinutes) return null;
    const schedule = boardBusinessHours || crmService.DEFAULT_BUSINESS_HOURS;
    const d = crmService.computeBusinessDeadline(cardInAt, slaLimitBusinessMinutes, schedule, boardHolidays);
    return d ? dayjs(d).format('DD/MM/YY HH:mm') : null;
  })();

  // Deadline for elapsed SLA — simple wall-clock addition.
  const slaDeadlineElapsed =
    cardInAt && slaLimitHours > 0
      ? dayjs(new Date(new Date(cardInAt).getTime() + slaLimitHours * 3_600_000)).format(
          'DD/MM/YY HH:mm'
        )
      : null;

  // Icon for the SLA badge:
  //   business SLA + inside hours  → ClockCircleOutlined (running)
  //   business SLA + outside hours → IoHandLeftOutline (frozen)
  //   elapsed SLA / no SLA         → ClockCircleOutlined
  const slaIcon =
    slaLimitBusinessMinutes > 0 ? (
      isInBizHours ? (
        <ClockCircleOutlined style={{ fontSize: 11 }} />
      ) : (
        <IoHandLeftOutline style={{ fontSize: 11 }} />
      )
    ) : (
      <ClockCircleOutlined style={{ fontSize: 11 }} />
    );

  // Primary badge: business-hours countdown. Tooltip: deadline.
  const slaBadge =
    slaLimitBusinessMinutes > 0
      ? buildSlaBadge(
          bizSecElapsed,
          slaLimitBusinessMinutes,
          elapsedMs,
          slaLimitHours,
          slaConfigTooltip,
          slaDeadlineBusiness,
          isInBizHours
        )
      : slaLimitHours > 0
        ? // Fallback: only elapsed-hours SLA configured — show wall-clock countdown
          buildWallClockBadge(elapsedMs, slaLimitHours, slaConfigTooltip, slaDeadlineElapsed)
        : null;

  // Movability: green bar if card can be moved, red bar otherwise
  const hasMissingRequiredFields = (() => {
    const formFields = board?.formFields || [];
    const required = formFields.filter(f => (f.requiredInColumns || []).includes(columnUid));
    if (!required.length) return false;
    const fd = card?.formData || {};
    return required.some(f => {
      const val = fd[f.fieldName];
      return val === undefined || val === null || val === '' ||
             (Array.isArray(val) && val.length === 0);
    });
  })();
  const canMove = isAttending && !isFiled && !hasMissingRequiredFields;

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. UI
  // ─────────────────────────────────────────────────────────────────────────────

  // Card body — shared between standalone and draggable render paths
  const cardBody = (
    <>
      {/* Top row: status bar (left) + deal outcome tag (right) */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <Tooltip
                title={
                  isFiled
                    ? translateX('filed')
                    : isRescheduled
                      ? translateX('rescheduled')
                      : !isAttending
                        ? translateX('not_attending')
                        : hasMissingRequiredFields
                          ? translateX('required_fields')
                          : translateX('attending')
                }
              >
                <div
                  style={{
                    width: 40,
                    height: 3,
                    borderRadius: 2,
                    backgroundColor: canMove ? '#76ff03' : '#ff1744',
                  }}
                />
              </Tooltip>

              {card.status === 'contract_closed' && (
                <Tag color="blue" variant="outlined" style={{ margin: 0, fontSize: 10 }}>
                  {translateX('closed_deal')}
                </Tag>
              )}
              {card.status === 'lost' && (
                <Tag color="red" variant="outlined" style={{ margin: 0, fontSize: 10 }}>
                  {translateX('lost_deal')}
                </Tag>
              )}
            </div>

            {/* Title */}
            <Typography.Text strong ellipsis style={{ display: 'block', marginBottom: 6 }}>
              {card.opportunity?.toUpperCase() || '-'}
            </Typography.Text>

            {/* Entity / Contact line */}
            {(entityName || personContactName) && (
              <div
                className="text-muted"
                style={{ fontSize: 12, marginBottom: 6, display: 'flex', alignItems: 'center' }}
              >
                {showPersonContact ? (
                  <>
                    <UserOutlined className="mr-1" />
                    <span>{toCapitalCase(personContactName)}</span>
                    <Blob />
                    <span>{toCapitalCase(entityName)}</span>
                  </>
                ) : (
                  <>
                    {isCompany ? <ShopOutlined className="mr-1" /> : <UserOutlined className="mr-1" />}
                    <span>{toCapitalCase(entityName || personContactName)}</span>
                  </>
                )}
              </div>
            )}

            {/* Middle row: indicators left + team avatars right */}
            <div
              className="d-flex align-items-center justify-content-between"
              style={{ marginTop: 4, minHeight: 24 }}
            >
              {/* Left: value, deadline, call count */}
              <div className="d-flex align-items-center" style={{ gap: 8, fontSize: 12 }}>
                {hasValue && (
                  <Tooltip title={`R$ ${card.approximateValue.toLocaleString('pt-BR')}`}>
                    <DollarOutlined className="text-success" />
                  </Tooltip>
                )}
                {hasDeadline && (
                  <Tooltip title={dayjs(card.deadline).format('DD/MM/YYYY')}>
                    <span
                      className="text-muted"
                      style={{ display: 'flex', alignItems: 'center', gap: 3 }}
                    >
                      <CalendarOutlined />
                      <span>{dayjs(card.deadline).format('DD/MM')}</span>
                    </span>
                  </Tooltip>
                )}
                {callCount > 0 && (
                  <span
                    className="text-muted"
                    style={{ display: 'flex', alignItems: 'center', gap: 3 }}
                  >
                    <CommentOutlined />
                    <span>{callCount}</span>
                  </span>
                )}
                {followUpCount > 0 && (
                  <span
                    className="text-muted"
                    style={{ display: 'flex', alignItems: 'center', gap: 3 }}
                  >
                    <MessageOutlined />
                    <span>{followUpCount}</span>
                  </span>
                )}
                {attachmentCount > 0 && (
                  <span
                    className="text-muted"
                    style={{ display: 'flex', alignItems: 'center', gap: 3 }}
                  >
                    <PaperClipOutlined />
                    <span>{attachmentCount}</span>
                  </span>
                )}
                {lifetimeText && (
                  <Tooltip title={`${translateX('lifetime')}: ${dayjs(createdAt).format('DD/MM/YY')}`}>
                    <span
                      className="text-muted"
                      style={{ display: 'flex', alignItems: 'center', gap: 3 }}
                    >
                      <HistoryOutlined />
                      <span>{lifetimeText}</span>
                    </span>
                  </Tooltip>
                )}
              </div>

              {/* Right: team member avatars */}
              <SimpleAvatarGroup
                items={teamMembers.map((m, i) => ({
                  key: m._id || i,
                  src: getMemberAvatarSrc(m),
                  name:
                    m.name ||
                    m.fullName ||
                    [m.firstName, m.lastName].filter(Boolean).join(' ') ||
                    '',
                  backColor: m.backColor,
                  foreColor: m.foreColor,
                }))}
                maxCount={3}
                size={22}
              />
            </div>

            {/* Footer: origin icon + creation/update dates + SLA badge */}
            <div
              className="d-flex align-items-center justify-content-between mt-1"
              style={{ fontSize: 11, color: '#8c8c8c' }}
            >
              <div>{buildOriginIcon(card, 13, isDark)}</div>
              <div
                className="d-flex align-items-end"
                style={{ flexDirection: 'column', gap: 2, textAlign: 'right' }}
              >
                <div className="d-flex align-items-center" style={{ gap: 6 }}>
                  {createdAt && (
                    <Tooltip title={translateX('created_date')}>
                      <span>{dayjs(createdAt).format('DD/MM/YY HH:mm')}</span>
                    </Tooltip>
                  )}
                  {updatedAt && updatedAt !== createdAt && (
                    <Tooltip title={translateX('last_update')}>
                      <span className="text-muted">
                        {dayjs(updatedAt).format('DD/MM/YY HH:mm')}
                      </span>
                    </Tooltip>
                  )}
                </div>
                {isRescheduled ? (
                  // Rescheduled row: [CalendarOutlined + date] left | [PauseCircleOutlined + paused] right
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 6 }}>
                    <Tooltip title={translateX('rescheduled_opportunity')} placement="left">
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, fontWeight: 600, color: '#1677ff' }}>
                        <CalendarOutlined style={{ fontSize: 11 }} />
                        <span>
                          {card?.attendanceRescheduledTo
                            ? dayjs(card.attendanceRescheduledTo).format('DD/MM/YY HH:mm')
                            : translateX('rescheduled')}
                        </span>
                      </div>
                    </Tooltip>
                    <Tooltip title={translateX('sla_paused')} placement="left">
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, fontWeight: 600, color: '#8c8c8c' }}>
                        <PauseCircleOutlined style={{ fontSize: 11 }} />
                        <span>{translateX('sla_paused')}</span>
                      </div>
                    </Tooltip>
                  </div>
                ) : slaBadge ? (
                  <Tooltip title={slaBadge.tooltip?.toLowerCase()}>
                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 3,
                        fontSize: 11,
                        fontWeight: 600,
                        color: slaBadge.color,
                        letterSpacing: 0.2,
                      }}
                    >
                      {slaIcon}
                      <span>{slaBadge.label}</span>
                    </div>
                  </Tooltip>
                ) : null}
              </div>
            </div>
    </>
  );

  // Standalone mode: plain card without Draggable, no actions, no navigation
  if (standalone) {
    return (
      <Card
        size="small"
        style={{
          borderRadius: 10,
          borderTop: tempColor ? `3px solid ${tempColor}` : undefined,
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
        }}
      >
        {cardBody}
      </Card>
    );
  }

  // Default: draggable card with full interactivity and action buttons
  return (
    <Draggable draggableId={card._id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          style={{
            ...provided.draggableProps.style,
            marginBottom: isLast ? 0 : 10,
          }}
        >
          <Card
            size="small"
            onClick={handleClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            actions={[
              // Button 1: Play / Pause / Stop based on card state
              isFinalized ? (
                <Tooltip key="action-stop" title={translateX(card.status === 'contract_closed' ? 'closed_deal' : 'lost_deal')}>
                  <CgPlayStopO style={{ fontSize: 16, color: '#8c8c8c', cursor: 'default' }} onClick={e => e.stopPropagation()} />
                </Tooltip>
              ) : isRescheduled ? (
                <Tooltip key="action-paused" title={translateX('rescheduled')}>
                  <CgPlayPauseO style={{ fontSize: 16, color: '#faad14', cursor: 'default' }} onClick={e => e.stopPropagation()} />
                </Tooltip>
              ) : (
                <Tooltip key="action-start" title={translateX(isAttending ? 'attending' : 'start_attendance')}>
                  <PlayCircleOutlined
                    onClick={isAttending ? e => e.stopPropagation() : handleStartAttendance}
                    style={isAttending ? { color: '#52c41a', cursor: 'default' } : undefined}
                  />
                </Tooltip>
              ),
              <Tooltip key="action-transfer" title={translateX('transfer_opportunity')}>
                <SwapOutlined
                  onClick={isFinalized ? e => e.stopPropagation() : handleTransferClick}
                  style={isFinalized ? { color: '#d9d9d9', cursor: 'default' } : undefined}
                />
              </Tooltip>,
              <Tooltip key="action-edit" title={translateX('edit')}>
                <EditOutlined onClick={handleEditClick} />
              </Tooltip>,
            ]}
            style={{
              cursor: 'pointer',
              borderRadius: 10,
              borderTop: tempColor ? `3px solid ${tempColor}` : undefined,
              overflow: 'hidden',
              marginBottom: isLast && 0,
              transition: 'box-shadow 0.2s ease, transform 0.2s ease',
              boxShadow: snapshot.isDragging
                ? '0 8px 24px rgba(0,0,0,0.22)'
                : isHovered
                  ? '0 6px 20px rgba(0,0,0,0.16)'
                  : '0 2px 8px rgba(0,0,0,0.10)',
              transform: snapshot.isDragging || !isHovered ? undefined : 'translateY(-2px)',
            }}
          >
            {cardBody}
          </Card>
        </div>
      )}
    </Draggable>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default KanbanCard;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function getMemberAvatarSrc(member) {
  const avatar = member?.avatar;
  return avatar?.base64Thumb || avatar?.thumbUrl || avatar?.storageUrl || member?.img || null;
}

// Builds badge for the business-hours SLA (primary).
function buildSlaBadge(
  bizSecElapsed,
  limitMinutes,
  _elapsedMs,
  _limitHours,
  _configTooltip,
  deadline,
  isInBizHours
) {
  const limitSec = limitMinutes * 60;
  const remainingSec = limitSec - bizSecElapsed;
  const pct = remainingSec / limitSec;
  const color = crmService.getSlaColor(pct);
  const label = crmService.formatDurationMs(remainingSec * 1_000);
  const statusLabel = isInBizHours
    ? translateX('time_running')
    : translateX('time_paused');
  const tooltip = deadline
    ? `${translateX('deadline')}: ${deadline} | ${translateX('business_hours')} | ${statusLabel}`
    : null;
  return { label, color, tooltip };
}

// Builds badge for wall-clock-only SLA (fallback when businessMinutes = 0).
function buildWallClockBadge(elapsedMs, limitHours, _configTooltip, deadline) {
  const limitMs = limitHours * 3600_000;
  const remainingMs = limitMs - elapsedMs;
  const pct = remainingMs / limitMs;
  const color = crmService.getSlaColor(pct);
  const label = crmService.formatDurationMs(remainingMs);
  const tooltip = deadline
    ? `${translateX('deadline')}: ${deadline} | ${translateX('elapsed_time')}`
    : null;
  return { label, color, tooltip };
}
